<template>
  <div class="favorite-edit-container">
    <h3 class="title">我的最愛</h3>
    
    <!-- 修改未登錄提示 -->
    <div v-if="!userStore.isLoggedIn" class="login-message">
      <i class="fas fa-exclamation-circle"></i>
      <h3>請先登入</h3>
      <p>您需要登入才能查看和管理我的最愛歌曲</p>
    </div>
    
    <!-- 歌曲列表 -->
    <div v-else class="song-list">
      <div v-if="loading" class="loading">
        加載中...
      </div>
      <div v-else-if="songs.length === 0" class="empty-message">
        暫無收藏歌曲
      </div>
      <div v-else class="song-items">
        <ul>
          <li v-for="song in currentPageSongs" 
              :key="song.id" 
              @click="showDetails(song)"
              @dblclick="addToPlaylistAndConfirm(song)"
              class="list-group-item">
            <div class="row align-items-center">
              <div class="col-md-4">
                <div class="video-wrapper2">
                  <img 
                    v-if="song.album"
                    :src="getAbsoluteUrl(song.album)"
                    class="preview-image"
                    :alt="song.songname"
                    @error="handleImageError"
                  >
                  <img 
                    v-else-if="isYoutubeUrl(song.filename)"
                    :src="getYoutubeThumbnail(song.filename)"
                    class="preview-image"
                    :alt="song.songname"
                    @error="handleImageError"
                  >
                  <div v-else class="video-fallback">
                    <div class="format-notice">
                      <span class="music-icon">♪</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="text-white">
                  <div>分類：{{ song.songclass }}</div>
                  <div>歌名：{{ song.songname }}</div>
                </div>
              </div>
              <div class="col-md-2">
                <button @click.stop="removeSong(song.id)" class="btn-delete">
                  刪除
                </button>
              </div>
            </div>
          </li>
          <!-- 分頁控制放在列表的最後一個項目中 -->
          <li v-if="songs.length > 0" class="list-group-item pagination-item">
            <div class="pagination-container">
              <button 
                class="pagination-btn prev"
                :class="{ 'disabled': currentPage === 1 }"
                @click.stop="prevPage"
              >
              </button>
              <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
              <button 
                class="pagination-btn next"
                :class="{ 'disabled': currentPage === totalPages }"
                @click.stop="nextPage"
              >
              </button>
            </div>
          </li>
        </ul>
      </div>
      <div class="bottom-spacer"></div>
    </div>
    <!-- <hr class="page-end-line">
    <hr class="page-end-line">
    <hr class="page-end-line">
      <br>
      <br>
      <br>
      <br>
      <br>
      <br> -->
    <!-- 確認刪除對話框 -->
    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content">
        <h3>確認刪除</h3>
        <p>確定要將此歌曲從我的最愛中移除嗎？</p>
        <div class="button-group">
          <button @click="confirmDelete" class="btn-confirm">確定</button>
          <button @click="cancelDelete" class="btn-cancel">取消</button>
        </div>
      </div>
    </div>

    <!-- 歌曲詳情彈窗 -->
    <div v-if="showModal" class="modal">
      <div class="modal-content">
        <span class="close" @click="closeModal">×</span>
        <p>歌曲名稱: {{ selectedSong?.songname }}</p>
        <!-- <p class="mb-0">通路＋版權費：本曲收費{{ selectedSong?.songChargeA }}+{{ selectedSong?.songChargeB }}元</p>
        <p>播放後收費，播放前可取消！</p> -->
        <button @click="confirmSelection">確認</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '../store/useUserStore';
import { usePlaylistStore } from '../store/usePlaylistStore';
import axiosInstance from '@/axios';
import { getCookie } from './csrf.js';
import { fixUrl, getYoutubeThumbnail, isYoutubeUrl } from '../utils/url-helper';

const userStore = useUserStore();
const playlistStore = usePlaylistStore();
const songs = ref([]);
const loading = ref(true);
const currentPage = ref(1);
const itemsPerPage = 10;
const showDeleteModal = ref(false);
const showModal = ref(false);
const songToDelete = ref(null);
const selectedSong = ref(null);

// 計算總頁數
const totalPages = computed(() => Math.ceil(songs.value.length / itemsPerPage));

// 計算當前頁的歌曲
const currentPageSongs = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return songs.value.slice(start, end);
});

// 顯示歌曲詳情
const showDetails = (song) => {
  selectedSong.value = song;
  showModal.value = true;
};

// 關閉詳情彈窗
const closeModal = () => {
  showModal.value = false;
};

// 確認添加到播放列表
const confirmSelection = async () => {
  try {
    playlistStore.addSong({
      id: selectedSong.value.id,
      songname: selectedSong.value.songname,
      filename: selectedSong.value.filename,
      stateA: false,
      stateB: false,
    });

    // 準備請求數據
    const requestData = {
      songsname: selectedSong.value.id,
    };
    
    // 如果用戶已登入，添加 customerno；否則允許未登入用戶點歌（customerno 為 null）
    const customerId = userStore.user?.customer_id;
    if (customerId) {
      // 從 'C104' 中提取數字 104
      const numericUserId = parseInt(customerId.replace('C', ''));
      if (!isNaN(numericUserId)) {
        requestData.customerno = numericUserId;
      } else {
        console.warn('無法解析用戶 ID:', customerId);
      }
    } else {
      console.log('用戶未登入，將創建無用戶 ID 的歌曲請求');
    }
    
    await axios.post('/songsrequest/create/', requestData);
    console.log('EditMyFavorite 歌曲請求:', requestData);

    showModal.value = false;
  } catch (error) {
    console.error('添加到播放列表失敗:', error);
  }
};

// 雙擊直接添加到播放列表
const addToPlaylistAndConfirm = async (song) => {
  try {
    playlistStore.addSong({
      id: song.id,
      songname: song.songname,
      filename: song.filename,
      stateA: false,
      stateB: false,
    });
    await axiosInstance.post('/songsrequest/create/', {
      songsname: song.id,
      customerno: 107,
    });
    alert('確認成功');
  } catch (error) {
    console.error('添加到播放列表失敗:', error);
  }
};

// 獲取收藏歌曲列表
const fetchFavoriteSongs = async () => {
  loading.value = true;
  try {
    const customerId = userStore.user?.customer_id;
    if (!customerId) {
      loading.value = false;
      songs.value = [];
      return;
    }
    
    const customerResponse = await axiosInstance.get(`/customer/${customerId}/balancepoints/`);
    const favoriteIds = customerResponse.data.favoritesongsID || [];
    
    if (favoriteIds.length === 0) {
      songs.value = [];
      loading.value = false;
      return;
    }

    const songPromises = favoriteIds.map(id => 
      axiosInstance.get(`/songs/${id}/`)
        .catch(error => {
          console.error(`獲取歌曲ID ${id} 失敗:`, error);
          return null;
        })
    );
    
    const responses = await Promise.all(songPromises);
    songs.value = responses
      .filter(response => response && response.data)
      .map(response => {
        const song = { ...response.data };
        
        // 預處理縮圖 URL
        if (song.album) {
          console.log('song.album ',song.album );
          try {
            let fixedUrl = song.album;
            
            // 處理 localhost/media/ 前綴問題
            if (fixedUrl.includes('localhost/media/')) {
              // 移除 localhost/media/ 前綴
              fixedUrl = fixedUrl.replace(/https?:\/\/localhost\/media\//, '');
            }
            
            // 處理編碼的 URL
            if (fixedUrl.includes('%3A')) {
              try {
                fixedUrl = decodeURIComponent(fixedUrl);
              } catch (e) {
                console.error('URL 解碼失敗:', e);
              }
            }
            
            // 處理雙重 https 問題
            if (fixedUrl.startsWith('https://localhost/media/https')) {
              fixedUrl = fixedUrl.replace('https://localhost/media/https', 'https');
            }
            
            // 移除其他錯誤的前綴
            if (fixedUrl.includes('backend:8000/media/')) {
              fixedUrl = fixedUrl.replace(/https?:\/\/backend:8000\/media\//, '');
            }
            if (fixedUrl.includes('singsong.cc/media/')) {
              fixedUrl = fixedUrl.replace(/https?:\/\/singsong.cc\/media\//, '');
            }
            
            // 確保是絕對 URL
            if (fixedUrl.includes('i.ytimg.com') || fixedUrl.includes('img.youtube.com')) {
              if (fixedUrl.startsWith('https:/') && !fixedUrl.startsWith('https://')) {
                fixedUrl = 'https://' + fixedUrl.substring(7);
              }
              if (!fixedUrl.match(/^https?:\/\//)) {
                fixedUrl = 'https://' + fixedUrl;
              }
            }
            
            console.log('song.album 2 ',fixedUrl );
            song.album = fixedUrl;
          } catch (error) {
            console.error('處理縮圖 URL 失敗:', error, song.album);
          }
        }
        
        return song;
      });
  } catch (error) {
    console.error('獲取收藏歌曲失敗:', error);
    songs.value = [];
  } finally {
    loading.value = false;
  }
};

// 移除歌曲
const removeSong = (songId) => {
  songToDelete.value = songId;
  showDeleteModal.value = true;
};

// 確認刪除
const confirmDelete = async () => {
  try {
    const customerId = userStore.user?.customer_id;
    if (!customerId) {
      alert('請先登入');
      return;
    }

    const response = await axiosInstance.get(`/customer/${customerId}/balancepoints/`);
    let favoriteList = response.data.favoritesongsID || [];
    favoriteList = favoriteList.filter(id => id !== songToDelete.value);
    
    await axiosInstance.post(
      `/customer/${customerId}/updatebalance/`,
      {
        favoritesongsID: favoriteList
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken')
        }
      }
    );
    
    songs.value = songs.value.filter(song => song.id !== songToDelete.value);
    
    if (currentPageSongs.value.length === 0 && currentPage.value > 1) {
      currentPage.value--;
    }
    
    showDeleteModal.value = false;
    songToDelete.value = null;
    // alert('成功從我的最愛中移除！');
  } catch (error) {
    console.error('刪除收藏失敗:', error);
    alert('刪除失敗，請稍後重試');
  }
};

// 取消刪除
const cancelDelete = () => {
  showDeleteModal.value = false;
  songToDelete.value = null;
};

// 翻頁方法
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// 修改 getImageUrl 函數
const getImageUrl = (song) => {
  if (!song) return null;
  
  if (song.filename && song.filename.includes('youtube.com')) {
    return getYoutubeThumbnail(song.filename);
  }
  
  if (song.album) {
    return song.album;
  }
  
  return null;
};

// 修改 handleImageError 函數
const handleImageError = (event) => {
  const src = event.target.src;
  console.log('圖片加載失敗，嘗試修復 URL:', src);
  
  // 檢查是否已經嘗試過修復
  if (src.includes('_fixed_attempt')) {
    console.log('已嘗試修復但仍然失敗，隱藏圖片');
    event.target.style.display = 'none';
    return;
  }
  
  try {
    // 處理雙重 https 問題
    let fixedUrl = src;
    if (fixedUrl.startsWith('https://https')) {
      fixedUrl = fixedUrl.replace('https://https', 'https');
    }
    
    // 處理編碼的 URL
    if (fixedUrl.includes('%3A')) {
      try {
        fixedUrl = decodeURIComponent(fixedUrl);
      } catch (e) {
        console.error('URL 解碼失敗:', e);
      }
    }
    
    // 使用 fixUrl 函數處理 URL
    fixedUrl = fixUrl(fixedUrl);
    
    // 確保是絕對 URL
    if (fixedUrl.includes('i.ytimg.com') || fixedUrl.includes('img.youtube.com')) {
      if (!fixedUrl.match(/^https?:\/\//)) {
        fixedUrl = 'https://' + fixedUrl;
      }
    }
    
    // 添加標記，表示已嘗試修復
    fixedUrl += (fixedUrl.includes('?') ? '&' : '?') + '_fixed_attempt=1';
    
    console.log('修復後的 URL:', fixedUrl);
    
    // 設置修復後的 URL
    event.target.src = fixedUrl;
  } catch (error) {
    console.error('URL 修復失敗:', error);
    event.target.style.display = 'none';
  }
};

// 獲取絕對 URL
const getAbsoluteUrl = (url) => {
  if (!url) return '';
  
  try {
    // 處理雙重 https 問題
    if (url.startsWith('https://https')) {
      url = url.replace('https://https', 'https');
    }
    
    // 處理編碼的 URL
    if (url.includes('%3A')) {
      try {
        url = decodeURIComponent(url);
      } catch (e) {
        console.error('URL 解碼失敗:', e);
      }
    }
    
    // 如果已經是完整的 YouTube 縮圖 URL，直接返回
    if (url.match(/^https?:\/\/(i\.ytimg\.com|img\.youtube\.com)/)) {
      return url;
    }
    
    // 使用 fixUrl 函數處理 URL
    const fixedUrl = fixUrl(url);
    
    // 確保是絕對 URL
    if (fixedUrl.includes('i.ytimg.com') || fixedUrl.includes('img.youtube.com')) {
      if (!fixedUrl.match(/^https?:\/\//)) {
        return 'https://' + fixedUrl;
      }
      return fixedUrl;
    }
    
    return fixedUrl;
  } catch (error) {
    console.error('URL 處理失敗:', error);
    return '';
  }
};

// 組件掛載時獲取數據
onMounted(fetchFavoriteSongs);
</script>

<style scoped>
.favorite-edit-container {
  padding: 10px;
  max-width: 800px;
  margin: 0 auto;
  height: calc(100vh - 100px); /* 設置容器高度，減去其他元素的高度 */
  overflow-y: auto; /* 允許垂直滾動 */
  -webkit-overflow-scrolling: touch; /* iOS 滾動優化 */
}

.song-list {
  margin-top: 0.5rem;
  height: 100%;
  overflow-y: auto;
}

.song-items {
  height: 100%;
  overflow-y: auto;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  overflow-y: auto;
}

/* 移除全局的滾動條隱藏 */
.favorite-edit-container::-webkit-scrollbar,
.song-list::-webkit-scrollbar,
.song-items::-webkit-scrollbar,
ul::-webkit-scrollbar {
  width: 5px;
  display: block;
}

.favorite-edit-container::-webkit-scrollbar-thumb,
.song-list::-webkit-scrollbar-thumb,
.song-items::-webkit-scrollbar-thumb,
ul::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.list-group-item {
  margin-bottom: 0.4rem;
  background-color: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.4rem;
  cursor: pointer;
}

.row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.col-md-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
}

.col-md-6 {
  flex: 0 0 50%;
  max-width: 50%;
}

.col-md-2 {
  flex: 0 0 16.666667%;
  max-width: 16.666667%;
  display: flex;
  justify-content: flex-end;
}

.text-white {
  color: white !important;
  font-size: 1rem;
  line-height: 1.5;
}

.video-wrapper2 {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: #000;
  overflow: hidden;
  margin-bottom: 0rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.video-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
}

.format-notice {
  color: white;
  text-align: center;
  padding: 1rem;
  font-size: 0.9rem;
  opacity: 0.8;
}

.music-icon {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.7);
}

.btn-delete {
  padding: 6px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
}

.btn-delete:hover {
  background-color: #c82333;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px;
  border-radius: 20px;
  width: fit-content;
  backdrop-filter: blur(5px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.pagination-item {
  background: transparent !important;
  border: none !important;
  cursor: default !important;
  padding: 10px 0 !important;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, #007bff, #0056b3);
  color: white;
  font-weight: normal;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.pagination-btn.prev::before {
  content: "←";
  font-size: 1.2rem;
  margin: 0;
}

.pagination-btn.next::after {
  content: "→";
  font-size: 1.2rem;
  margin: 0;
}

.pagination-btn:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.pagination-btn.disabled {
  background: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.7;
}

.page-info {
  font-size: 0.9rem;
  color: white;
  padding: 0.3rem 0.8rem;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  margin: 0 0.5rem;
  min-width: 60px;
  text-align: center;
}

.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;  /* 確保顯示在最上層 */
}

.modal-content {
  background-color: #6488b0;
  padding: 20px;
  border-radius: 5px;
  width: 80%;
  max-width: 500px;
  color: white;  
  z-index: 10001;  /* 比背景更高一層 */
}

.close {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: white;
}

.title {
  color: white;
  text-align: left;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
}

.login-message {
  text-align: center;
  padding: 1rem;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  margin: 0.5rem auto;
  max-width: 400px;
}

.loading, .empty-message {
  color: white;
  text-align: center;
  padding: 20px;
}

/* 手機版樣式優化 */
@media (max-width: 768px) {
  .favorite-edit-container {
    height: calc(100vh - 400px);
    overflow-y: auto;
  }

  .col-md-4, .col-md-6, .col-md-2 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .list-group-item {
    padding: 0.25rem;
    height: auto;
    overflow: hidden;
  }

  .video-wrapper {
    width: 100%;
  }
  
  /* 確保在手機上可以滾動 */
  .song-items {
    height: auto;
    overflow-y: visible;
  }
}

.page-end-line {
  border: 1;
  height: 10px;
  background: rgb(50, 10, 10);
  margin-bottom: -13rem 0;
}
</style> 